import { ChevronDown, Loader2, Search, X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface AsyncSearchableSelectOption {
  value: string;
  label: string;
  /** Optional secondary line, usually email or org. Renders muted under the label. */
  hint?: string;
  /**
   * Optional leading avatar / badge for the option row. Rendered inside a
   * 32px square slot so a round <Avatar> or custom SVG both look right.
   * If omitted the row just shows label + hint (current behavior).
   */
  avatar?: ReactNode;
}

export interface AsyncSearchableSelectProps {
  label: string;
  value: string | null;
  /** Caller-supplied label for the currently selected value (since options are async). */
  selectedLabel?: string;
  /** Optional caller-supplied secondary line for the selected state (e.g. email). */
  selectedHint?: string;
  /** Optional caller-supplied avatar for the selected state (same slot as option.avatar). */
  selectedAvatar?: ReactNode;
  /**
   * Fired on select (with the chosen value + full option) and on clear (with
   * `null` and no option). The `option` lets callers persist the human label
   * for the selected value, since options are loaded async and otherwise lost.
   */
  onChange: (value: string | null, option?: AsyncSearchableSelectOption) => void;
  loadOptions: (search: string) => Promise<AsyncSearchableSelectOption[]>;
  placeholder?: string;
  /** Shown in the dropdown when the popover is open but no search has been typed yet. */
  emptyHint?: string;
  /** Search input debounce; defaults to 300ms to match the rest of the FilterBar. */
  debounceMs?: number;
  className?: string;
  /** Compact mode hides the uppercase label (for inset toolbars). */
  compact?: boolean;
  /** When true the trigger is non-interactive and greyed (e.g. a Location
   *  picker that cascades from an as-yet-unselected Organization). */
  disabled?: boolean;
}

/**
 * Single-select async picker for FilterBar use. Mirrors the chrome of
 * `<MultiSelectFilter>` (popover w/ search + scroll list) but loads options
 * from a caller-supplied `loadOptions(search)` promise.
 *
 * Visual: §12 of the visual design spec — h-10 trigger, w-72 popover, brand
 * tokens throughout. Keyboard: ↑/↓ move focus, Enter selects, Esc closes.
 */
export function AsyncSearchableSelect({
  label,
  value,
  selectedLabel,
  selectedHint,
  selectedAvatar,
  onChange,
  loadOptions,
  placeholder,
  emptyHint,
  debounceMs = 300,
  className,
  compact = false,
  disabled = false,
}: AsyncSearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [options, setOptions] = useState<AsyncSearchableSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const reqIdRef = useRef(0);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const labelId = useId();

  // Hold the latest loadOptions in a ref so the fetch effect can call it
  // WITHOUT listing it as a dependency. Callers routinely pass a fresh closure
  // every render (and some, like the book-session form, mutate parent state
  // inside it), so depending on its identity would refire the effect on every
  // render → fetch → parent render → refire, an infinite request loop.
  const loadOptionsRef = useRef(loadOptions);
  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  });

  // Debounce raw query → debouncedQuery. The setState calls below are
  // intentional: scheduling a deferred update + clearing on the no-debounce path.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (debounceMs === 0) {
      setDebouncedQuery(query);
      return;
    }
    const t = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);
    return () => {
      clearTimeout(t);
    };
  }, [query, debounceMs]);

  // Fire loadOptions whenever the popover is open and the debounced query
  // changes. Empty query is intentional: callers return their default
  // "first N" list so the user sees suggestions on open without having to
  // type anything. setState here reflects async I/O state (loading /
  // results / error), which is the canonical effect-with-setState pattern.
  useEffect(() => {
    if (!open) return;
    const id = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    loadOptionsRef
      .current(debouncedQuery)
      .then((res) => {
        // Drop stale responses if the user has typed again.
        if (id !== reqIdRef.current) return;
        setOptions(res);
        setLoading(false);
        setFocusIdx(res.length > 0 ? 0 : -1);
      })
      .catch((err: unknown) => {
        if (id !== reqIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Could not load results');
        setLoading(false);
      });
  }, [debouncedQuery, open]);

  // Reset transient state on close + focus the input on open. setState is
  // intentional — this responds to the parent-controlled `open` prop changing.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setOptions([]);
      setFocusIdx(-1);
      setError(null);
      return;
    }
    // Defer one tick so the portal mounts before we grab focus.
    const t = setTimeout(() => {
      searchRef.current?.focus();
    }, 0);
    return () => {
      clearTimeout(t);
    };
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  function selectOption(opt: AsyncSearchableSelectOption) {
    onChange(opt.value, opt);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[focusIdx];
      if (opt) selectOption(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const triggerLabel = useMemo(() => {
    if (value && selectedLabel) return selectedLabel;
    return placeholder ?? label;
  }, [value, selectedLabel, placeholder, label]);

  const hasValue = Boolean(value);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {compact ? (
        <span id={labelId} className="sr-only">
          {label}
        </span>
      ) : (
        <span
          id={labelId}
          className="text-meta-sm tracking-label font-medium text-neutral-500 uppercase"
        >
          {label}
        </span>
      )}
      <div
        className={cn(
          // Chrome mirrors `SelectTrigger` (src/components/ui/select.tsx) so a
          // row of mixed Select + AsyncSearchableSelect filters reads as one
          // system: same border, same h-10, same px-3, same text size.
          'group/trigger bg-surface flex items-center rounded-md border border-(--color-input) pr-2 pl-3',
          'text-accent-ink min-w-45 text-sm',
          'transition-[border-color,box-shadow] duration-150',
          disabled
            ? 'cursor-not-allowed bg-neutral-50 opacity-60'
            : 'hover:border-primary-200 focus-within:border-primary-300 focus-within:shadow-(--shadow-focus-ring)',
          // Compact mode keeps a uniform 40px row for filter-bar alignment; the
          // expanded "selected name + email + avatar" view would break a row of
          // other h-10 filters the moment a user picks a value.
          compact || !hasValue ? 'h-10' : 'min-h-14 py-1.5',
        )}
      >
        <Popover open={disabled ? false : open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-labelledby={labelId}
              aria-expanded={open}
              aria-controls={`${labelId}-listbox`}
              disabled={disabled}
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2 text-left',
                'focus-visible:outline-none disabled:cursor-not-allowed',
              )}
            >
              {hasValue && selectedAvatar && !compact ? (
                <span
                  aria-hidden
                  className="inline-flex size-9 shrink-0 items-center justify-center"
                >
                  {selectedAvatar}
                </span>
              ) : null}
              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cn(
                    'truncate',
                    hasValue ? 'text-accent-ink font-medium' : 'text-neutral-400',
                  )}
                >
                  {triggerLabel}
                </span>
                {hasValue && selectedHint && !compact ? (
                  <span className="text-meta-sm truncate text-neutral-500">{selectedHint}</span>
                ) : null}
              </span>
              {!hasValue ? (
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 opacity-50 transition-transform duration-150',
                    open && 'rotate-180',
                  )}
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : null}
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            sideOffset={6}
            className="p-0"
            style={{
              minWidth: 'var(--radix-popover-trigger-width)',
              width: 'max(var(--radix-popover-trigger-width, 18rem), 18rem)',
            }}
          >
            <div className="flex items-center gap-2 border-b border-(--color-border) bg-neutral-50/60 px-3 py-2.5">
              <Search className="h-4 w-4 text-neutral-400" strokeWidth={1.5} aria-hidden="true" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={`Search ${label.toLowerCase()}…`}
                className={cn(
                  'text-body-sm text-accent-ink w-full bg-transparent placeholder:text-neutral-400 focus:outline-none',
                  // Hide the browser-native search cancel button — it inherits
                  // `text-accent-ink` and renders as a stark teal X that
                  // clashes with the muted popover chrome. Query clears on
                  // popover close, so no custom affordance is needed.
                  '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
                )}
              />
              {loading ? (
                <Loader2
                  className="text-primary-500 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
              ) : null}
            </div>

            <div
              id={`${labelId}-listbox`}
              role="listbox"
              aria-label={label}
              className="max-h-64 overflow-y-auto py-1"
            >
              {error ? (
                <div className="text-body-sm text-error-700 px-3 py-5 text-center">{error}</div>
              ) : loading && options.length === 0 ? (
                <div className="text-body-sm flex items-center justify-center gap-2 px-3 py-5 text-center text-neutral-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} aria-hidden />
                  {debouncedQuery ? 'Searching…' : 'Loading…'}
                </div>
              ) : options.length === 0 ? (
                <div className="text-body-sm px-3 py-5 text-center text-neutral-500">
                  {debouncedQuery
                    ? `No matches for “${debouncedQuery}”.`
                    : (emptyHint ?? 'No options available.')}
                </div>
              ) : (
                options.map((opt, i) => {
                  const active = i === focusIdx;
                  const selected = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseEnter={() => {
                        setFocusIdx(i);
                      }}
                      onClick={() => {
                        selectOption(opt);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors duration-150',
                        active && 'bg-primary-50/50',
                        selected && 'bg-primary-50/80',
                      )}
                    >
                      {opt.avatar ? (
                        <span
                          aria-hidden
                          className="inline-flex size-8 shrink-0 items-center justify-center"
                        >
                          {opt.avatar}
                        </span>
                      ) : null}
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span
                          className={cn(
                            'text-body-sm truncate',
                            selected ? 'text-accent-ink font-medium' : 'text-neutral-900',
                          )}
                        >
                          {opt.label}
                        </span>
                        {opt.hint ? (
                          <span className="text-meta-sm truncate text-neutral-500">{opt.hint}</span>
                        ) : null}
                      </span>
                      {selected ? (
                        <span
                          aria-hidden
                          className="bg-primary-500 h-1.5 w-1.5 shrink-0 rounded-full"
                        />
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>

        {hasValue && !disabled ? (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => {
              onChange(null);
            }}
            className={cn(
              // Matches the project's clear-X convention (see TextFilter): quiet
              // neutral tint, not brand teal, so clearing reads as a utility —
              // never competes with the selected value it's sitting beside.
              'inline-flex size-5 shrink-0 items-center justify-center rounded-full',
              'text-neutral-400 transition-colors duration-150',
              'hover:bg-neutral-100 hover:text-neutral-700',
              'focus-visible:ring-primary-200 focus-visible:text-neutral-700 focus-visible:ring-2 focus-visible:outline-none',
            )}
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
